import {
  defineComponent,
  h,
  onMounted,
  ref,
  resolveComponent,
  computed,
} from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useStore } from 'vuex'

import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue'
import nav from '@/_nav.js'

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)

  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  setup() {
    const route = useRoute()
    const store = useStore()
    const firstRender = ref(true)

    onMounted(() => {
      firstRender.value = false
    })

    // Create a computed property that filters the nav items
    const filteredNav = computed(() => {
      let isAuthenticated = store.getters.isAuthenticated
      let userRole = false
      let isInSetupMode = false
      if (isAuthenticated) {
        userRole = store.getters.userRole // Get the user's role
        isInSetupMode = store.getters.isInSetupMode // Get status of setup mode
      }

      return nav.filter((item) => {
        // --- HIDE CONDITIONS ---

        // 1. If it's a setup item (item.setup is true)
        //    and the user is NOT in setup mode, HIDE it.
        if (item.setup && !isInSetupMode) {
          return false
        }

        // 2. If it's a role-protected item (item.roles exists)
        //    and the user's role is NOT in the list, HIDE it.
        if (item.roles && !item.roles.includes(userRole)) {
          return false
        }

        // --- SHOW CONDITION ---
        // 3. If it wasn't hidden by any of the rules above, SHOW it.
        //    This covers public items (like 'Help') and items that
        //    passed their setup/role checks.
        return true
      })
    })

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon &&
                        h(resolveComponent('CIcon'), {
                          customClassName: 'nav-icon',
                          name: item.icon,
                        }),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => filteredNav.value.map((item) => renderItem(item)),
        },
      )
  },
})
export { AppSidebarNav }
